import { NgForm } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild, ViewChildren, ViewEncapsulation } from '@angular/core';

import { ChatPatientsTabType, ChatReadEvent, MediaType, PatientChat, PatientChatCollection, PatientChatStateEvent } from '../../../store/chat-store/chat.state';
import { chatSendSms, invokeMarkAsRead, invokePatientChatCollection, resetChosenPatientChat, resetPatientChatFromMarkAsReadSignalR, resetPatientChatFromSignalR } from '../../../store/chat-store/chat.action';
import { BaseDestroyComponent } from 'src/app/features/shared/components/base-destroy/base-destroy.component';
import { AuthService } from 'src/app/features/user/services/authentication/auth.service';
import { selectChatPageStateEvent, selectChatReadEvent, selectChosenPatientChat, selectPatientChatCollections, selectTrackingPatientChat } from '../../../store/chat-store/chat.selector';
import { DateUtilService } from 'src/app/features/shared/services/date-util.service';
import { PatientCommunicationActionService } from '../../../service/patient-communication-action-service';
import { PatientCommunication } from '../../../constant/patient-communication.constant';
import { ChatHistoryService } from '../../../service/chat-history-service';
import { CustomValidatorService } from 'src/app/features/shared/utils/custom-validator.service';
import { uploadType } from 'src/app/features/document/constant/document-message.constant';
import { DocumentUploadState } from 'src/app/features/document/store/document-upload-store/document-upload.state';
import { patientChatFile, PatientChatUploadInputModel } from '../../../store/patient-communication.state';
import { PopupModalComponent } from 'src/app/features/shared/components/modal/popup-modal.component';
import { arrAllowedDocTypes, arrAllowedImageTypes, arrAllowedVideoTypes } from '../../../constant/media-types';

export interface MenuItem {
  id: number,
  label: string;
  subItems?: MenuItem[];
}

@Component({
  selector: 'app-chat-contents',
  templateUrl: './chat-contents.component.html',
  styleUrls: ['./chat-contents.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class ChatContentsComponent extends BaseDestroyComponent  implements OnInit {
  public readonly ChatPatientsTabType: any = ChatPatientsTabType;
  public readonly MediaType: any = MediaType;

  @Input() public patientCommunication!: any;
  @Input() public popupIndex!: number;
  @Output() onClose = new EventEmitter<number>();

  @ViewChild('msgForm') msgForm!: NgForm;
  @ViewChildren('msgInput') msgInput:any;
  @ViewChild('documentUploadModal', { static: false }) documentUploadModal!: PopupModalComponent;
  
  activeContact: any;
  public oCurrentUser: any;
  public chatCollection: any;
  loadingCollection = true;
  minimizeChat: boolean = false;
  items: MenuItem[] = [];
  patientCommunications: PatientCommunication[] = [];
  showDocumentUpload = false;
  chatImage = uploadType.chatImage;
  patientDetails!: PatientChatUploadInputModel;
  mediaModel: { media: string[], file: patientChatFile[] } = { media: [], file: [] };

  allowedImageTypes = arrAllowedImageTypes;
  allowedVideoTypes = arrAllowedVideoTypes;
  allowedDocumentTypes = arrAllowedDocTypes;
  allChatCollections: PatientChat[] = [];


  activePatientChat$= this.store.select(selectChosenPatientChat);
  selectPatientChatCollections$= this.store.select(selectPatientChatCollections);
  chatPageStateEvent$ = this.store.select(selectChatPageStateEvent);
  selectTrackingPatientChat$ = this.store.select(selectTrackingPatientChat);
  selectChatReadEvent$ = this.store.select(selectChatReadEvent);
  isArchived = false;

  constructor(
    private store : Store,
    private authService: AuthService,
    private dateUtilService: DateUtilService,
    private patientCommunicationActionService: PatientCommunicationActionService,
    private chatHistoryService: ChatHistoryService) {
    super();
  }

  initMsgForm() {
    setTimeout(() => {
      this.msgForm?.reset();
      this.msgInput.first.nativeElement.focus();
      this.scrollToBottom();
    });
  }

  ngOnInit() {
    this.bindActivePaitentChat(this.patientCommunication);
    this.subscriptions$.add(this.chatPageStateEvent$.subscribe((data?: PatientChatStateEvent) => this.handleChatPageEventState(data)));
    this.subscriptions$.add(this.selectPatientChatCollections$.subscribe((data?: PatientChatCollection) => this.bindPaitentChatCollection(data)));
    this.subscriptions$.add(this.selectTrackingPatientChat$.subscribe((data?: PatientChat | null) => { if (data) { this.bindTrackingPatientChat(data); } }));
    this.subscriptions$.add(this.patientCommunicationActionService.patientCommunications$.subscribe((data: PatientCommunication[]) => this.bindPatientCommunication(data)));
    this.subscriptions$.add(this.selectChatReadEvent$.subscribe((data?: ChatReadEvent| null) => this.bindChatReadEvent(data)));
  }
  
  bindActivePaitentChat(data?: any) {
    if (data) {
      this.activeContact = data;
      this.patientDetails = {
        patientId: this.activeContact.patientId,
        primaryPhone: this.activeContact.patientPrimaryPhone,
        practiceId: this.authService.getCurrentPracticeId(),
        practiceLocationId: this.authService.getLocationIdFromToken()
      }
      this.store.dispatch(invokePatientChatCollection({ patientId: this.activeContact.patientId, patientPhone: this.activeContact.patientPrimaryPhone}))
    }
  }

  async handleChatPageEventState(data: PatientChatStateEvent | undefined) {
    if (!data) return;
    if (data && (data.patientPhone === this.activeContact.patientPrimaryPhone))
      this.loadingCollection = data.patientChatloading ?? false
  }

  bindPaitentChatCollection(data?: PatientChatCollection) {
    if (data && this.activeContact && (data.patientPhone === this.activeContact.patientPrimaryPhone)) {
      this.allChatCollections = data.patients ?? [];
      this.chatCollection = data.patients.filter(chatCollection => !chatCollection.isArchived) ?? [];
      this.updateChatView();
    }
  }

  bindTrackingPatientChat(data: PatientChat) {
    if (data && this.activeContact && (data.patientPhone === this.activeContact.patientPrimaryPhone) && this.chatCollection) {

      this.isArchived = false;
      
      const formatDateWithTime = this.chatHistoryService.getFormattedDateWithTime(data.time);
      const updatedPatientChat: any = { ...data, time: formatDateWithTime }
      this.allChatCollections = [...this.allChatCollections, updatedPatientChat];

      this.chatCollection = this.allChatCollections.filter(chatCollection => !chatCollection.isArchived) ?? [];

      this.updateChatView();
      this.store.dispatch(resetPatientChatFromSignalR({ patientChat: data }));
    }
  }

  bindPatientCommunication(data: PatientCommunication[]) {
    this.patientCommunications = data;
    const indexes = this.patientCommunications
      .map((communication, index) =>
        communication.communicationType === 'PatientChat' &&
          communication.patientPrimaryPhone === this.activeContact.patientPrimaryPhone ? index : -1)
      .find(index => index !== -1);
    this.popupIndex = indexes ?? 0;
  }

  sendMessage(e:any) {
    let bIsEmptyString = true;

    if (this.mediaModel.file.length === 0) {
      for (let i = 0; i < this.msgForm.form.value.message.length; i++) {
        if (this.msgForm.form.value.message[i] !== '\n') {
          bIsEmptyString = false;
          break;
        }
      }
    }
    if (bIsEmptyString && this.mediaModel.file.length == 0) {
      return;
    }

    const chat = {
      isOutbound: true,
      textMessage: this.msgForm.form.value.message ?? '',
      time: this.dateUtilService.GetApplicationCurrentDateTime(),
    };

    this.msgForm?.reset();
    const phone = this.activeContact.patientPrimaryPhone;
    const media = this.mediaModel.media;
    const file = this.mediaModel.file;
    this.store.dispatch(chatSendSms({communicationSms: {
      patientId: this.activeContact?.patientId, toNumber: phone, text: chat.textMessage, practiceLocationId: this.authService.getLocationIdFromToken(), media: media,
      practiceId: this.authService.getCurrentPracticeId(), files:file
    }, patientChat : {
      ...chat,
      patientChatCollectionId: 0,
      patientId:  this.activeContact?.patientId,
      practiceId: this.authService.getCurrentPracticeId(),
      isRead: false,
      isInbound: false,
      isMMS: false,
      fileHtml: '',
      fileName: '',
      url: '',
      isArchived: false,
      files: [],
      patientPhone: this.activeContact?.patientPrimaryPhone
    }}));
    this.initMsgForm();
    this.resetMediaFiles();
  }

  @ViewChild('bottomElement') bottomElementRef!: ElementRef;
  scrollToBottom() {
    setTimeout(() => {
      if (this.bottomElementRef && this.bottomElementRef.nativeElement)
        this.bottomElementRef.nativeElement.scrollTop = this.bottomElementRef.nativeElement.scrollHeight;
    }, 100);
  }

  public fnHandleMenuItemClick(item: MenuItem) {
    setTimeout(() => {
      const chat = {
        isOutbound: true,
        textMessage: item.label,
        time: this.dateUtilService.GetApplicationCurrentDateTime(),
      };

      const phone = this.activeContact.patientPrimaryPhone;

      this.store.dispatch(chatSendSms({communicationSms: {
        patientId: this.activeContact?.patientId, toNumber: phone, text: chat.textMessage, practiceLocationId: this.authService.getLocationIdFromToken(), media: this.mediaModel.media,
        practiceId: this.authService.getCurrentPracticeId()
      }, patientChat : {
        ...chat,
        patientChatCollectionId: 0,
        patientId:  this.activeContact?.patientId,
        practiceId: this.authService.getCurrentPracticeId(),
        isRead: false,
        isInbound: false,
        isMMS: false,
        fileHtml: '',
        fileName: '',
        url: '',
        isArchived: false,
        files: [],
        patientPhone: this.activeContact?.patientPrimaryPhone
      }}));
      this.initMsgForm();
    }, 0);
  }

  public fnGetFileType(oChat: any): MediaType | void {
    if (!oChat || !oChat.url) {
      return MediaType.Unsupported;
    }
    const url = oChat.url;
    const lastDotIndex = url.lastIndexOf('.');
    if (lastDotIndex === -1) {
      return MediaType.Unsupported;
    }
    const extension = url.substring(lastDotIndex).split('?')[0].toLowerCase();
    if (arrAllowedImageTypes.includes(extension)) {
      return MediaType.Image;
    } else if (arrAllowedVideoTypes.includes(extension)) {
      return MediaType.Video;
    } else if (arrAllowedDocTypes.includes(extension)) {
      return MediaType.Document;
    }
    return MediaType.Unsupported;
  }

  public openImageInNewTab(url: string): void {
    window.open(url, "_blank");
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.store.dispatch(resetChosenPatientChat())
  }

  closeModal() {
    this.onClose.emit(this.popupIndex);
  }

  minimizeChatPopup() {
    this.minimizeChat = !this.minimizeChat;
  }


  private setSeparatorDate() {
    let separatorDate = '';
    this.chatCollection = this.chatCollection.map((message: PatientChat) => {
      const messageDate = this.chatHistoryService.formatDateToDDMonthYYYY(new Date(message.time));
      if (separatorDate !== messageDate) {
        separatorDate = messageDate;
        return {
          separatorStarted: true,
          separatorDate: this.chatHistoryService.getChatSeparatorByDate(new Date(messageDate)),
          ...message
        };
      }
      return { separatorStarted: false, separatorDate: '', ...message };
    });
  }

  private setSeparatorForPatientAvatarsByDate() {
    let separatorDate = '';
    this.chatCollection = this.chatCollection.map((message: PatientChat) => {
      const messageDate = message.time.toString();
      if (separatorDate !== messageDate && message.isInbound) {
        separatorDate = messageDate;
        return {
          separatorPatientAvatarStarted: true,
          ...message
        };
      }
      return { separatorPatientAvatarStarted: false, ...message };
    });
  }

  private setSeparatorForPracticeAvatarsByDate() {
    let separatorDate = '';
    this.chatCollection = this.chatCollection.map((message: PatientChat) => {
      const messageDate = message.time.toString();
      if (separatorDate !== messageDate && message.isOutbound) {
        separatorDate = messageDate;
        return {
          separatorPracticeAvatarStarted: true,
          ...message
        };
      }
      return { separatorPracticeAvatarStarted: false, ...message };
    });
  }

  private setUnreadStatus() {
    let isUnreadStarted = false;
    this.chatCollection = this.chatCollection.map((message: PatientChat) => {
      if (!isUnreadStarted && !message.isRead && message.isInbound) {
        isUnreadStarted = true;
        return { isUnreadStarted: true, ...message };
      }
      return { isUnreadStarted: false, ...message };
    });
  }

  
  public updateChatView(): void {
    this.setSeparatorDate();
    this.setUnreadStatus();
    this.setSeparatorForPatientAvatarsByDate();
    this.setSeparatorForPracticeAvatarsByDate();
    this.scrollToBottom();
  }

  onMessageTextareaClick() {
    if (this.chatCollection && this.chatCollection.filter((_: PatientChat) => _.isUnreadStarted).length > 0)
      this.store.dispatch(invokeMarkAsRead({
        chatReadEvent: {
          phoneNumber: this.activeContact?.patientPrimaryPhone,
          patientId: 0,
          practiceId: this.authService.getCurrentPracticeId(),
          practiceLocationId: this.authService.getLocationIdFromToken(),
          patientChatCollectionId: 0
        }
      }));
  }

  bindChatReadEvent(data?: ChatReadEvent | null) {
    if (data && this.activeContact && (data.phoneNumber === this.activeContact.patientPrimaryPhone) && this.chatCollection) {
      this.chatCollection = this.chatCollection.map((message: PatientChat) => {
        if (!message.isRead)
          return { ...message, isRead: true, isUnreadStarted: false };
        return { ...message };
      });
      this.updateChatView();
      this.store.dispatch(resetPatientChatFromMarkAsReadSignalR());
    }
  }

  onCallClick() {
    if (!this.activeContact.patientPrimaryPhone) return;
    const patientCommunication: PatientCommunication = {
      communicationType: 'PatientCallDial',
      patientFirstName: this.activeContact.patientFirstName,
      patientLastName: this.activeContact.patientLastName,
      patientName: `${this.activeContact.patientLastName}, ${this.activeContact.patientFirstName}`,
      patientId: this.activeContact.patientId,
      patientPrimaryPhone: CustomValidatorService.removeMobileNumberFormatting(this.activeContact.patientPrimaryPhone) ?? '',
    }
    this.patientCommunicationActionService.onCallClick(patientCommunication);
  }

  updateDocuments(images: DocumentUploadState) {
    if (this.documentUploadModal) this.documentUploadModal.closeModal();
    const postModel = {
      media: images.documents.map((file: any) => file.documentUploadResponse.url),
      file: images.documents.map((file: any) => ({
        url: file.documentUploadResponse.url,
        fileName: file.documentUploadResponse.fileName
      }))
    };
  
    this.mediaModel = postModel;
  }

  removeUploadedImage(imgUrl: string) {
    this.mediaModel.media = this.mediaModel.media.filter((media: string) => media !== imgUrl);
    this.mediaModel.file = this.mediaModel.file.filter((file: patientChatFile) => file.url !== imgUrl);
  }

  resetMediaFiles(){
    this.mediaModel = {
      media: [],
      file: []
    };
  }

  showArchived(isArchived: boolean) {
    if(isArchived) {
      this.chatCollection = this.allChatCollections.filter(chatCollection => chatCollection.isArchived) ?? [];
      this.updateChatView();
    }
    else {
      this.chatCollection = this.allChatCollections.filter(chatCollection => !chatCollection.isArchived) ?? [];
      this.updateChatView();
    }
  }
  
}

